<template>
  <div class="d-flex flex-column h-100">
    <div
      v-if="loading || shouldShowLoaderForGeneration"
      class="flex-grow-1 d-flex align-center justify-center"
    >
      <Preloader
        :text="loadingText !== null && loadingText !== undefined ? loadingText : $t('pleaseWaitAiLoaderText')"
      />
    </div>
    <div v-else class="d-flex flex-column flex-grow-1">
      <div v-if="aiQuestions" class="mb-6 flex-grow-1">
        <VCard class="grey lighten-5 pa-0 pa-md-6 mt-6 mb-2" elevation="0">
          <h2 class="step-title">
            {{ $t('congrats') }}
          </h2>
          <h4 v-if="!disabled" class="step-subtitle mb-0 mb-md-4">
            {{ $t('anyTime') }}
          </h4>
          <WizardCounts :questions="aiQuestions.length" class="px-4 px-md-0" />
          <Draggable
            v-model="aiQuestions"
            class="mb-0 mb-md-6 mx-3"
            :handle="($vuetify.breakpoint.smAndDown || focusInput) ? '.handle' : false"
          >
            <VRow
              v-for="(aiQuestion, index) of aiQuestions"
              :key="`${aiQuestion.type}-${aiQuestion.id}`"
              class="q-row"
              :class="{
                'q-row__text-active': focusInput === aiQuestion.id,
                'exceed-limit': index >= maximumAiQuestions
              }"
            >
              <VacancyAiQuestionRow
                :ai-question="aiQuestion"
                :index="index"
                :focus-input="focusInput"
                :disabled="disabled"
                :exceed-limit-question="index >= maximumAiQuestions"
                @setFocusInput="setFocusInput"
                @showDestroyDialog="showDestroyDialog"
                @confirmAiQuestionChanges="confirmAiQuestionChanges"
              />
            </VRow>
          </Draggable>

          <VBtn
            text
            color="primary"
            class="mx-auto mx-md-0 mb-2"
            elevation="0"
            :disabled="disabled"
            @click="addQuestion"
          >
            <VIcon color="primary" small>
              mdi-plus
            </VIcon>
            <span class="pseudo-link primary--text d-inline-block ml-1 body-2">
              {{ $t('addQuestion') }}
            </span>
          </VBtn>
        </VCard>
        <VCard
          v-if="aiQuestionsLimited"
          class="limit-questions grey lighten-5 mx-md-0 pa-4 mb-2"
          elevation="0"
        >
          <div>
            <img :src="require('@/assets/img/warning-red.svg')" alt="warning-red">
          </div>
          <div class="limit-questions__main-wrap">
            <div class="limit-questions__main-wrap_title">
              {{ $tc('maxQuestionsAvailable', maximumAiQuestions, { count: maximumAiQuestions }) }}
            </div>
            <div class="limit-questions__main-wrap_text">
              {{ $t('maxQuestionsAvailableText') }}
            </div>
          </div>
        </VCard>
      </div>

      <VacancyQuestionDestroyDialog
        @close="closeDestroyDialog"
        v-model="destroyDialog"
        @destroy="deleteAiQuestion"
      />

      <VacancyPresetInterviewers
        v-if="avatars && vacancy && vacancy.id"
        v-model="avatarDialog"
        preset="aiInterview"
        :avatars="avatars"
        :vacancy-id="vacancy.id"
        :current-avatar="aiInterviewer"
        @selected="handleAvatarSelected"
      />

      <div class="mt-auto">
        <slot
          name="backButton"
          :disabled="buttonBackDisabled"
        >
          <VBtn
            color="success"
            :disabled="buttonBackDisabled"
            elevation="0"
            :loading="loading"
          >
            {{ $t('back') }}
          </VBtn>
        </slot>

        <VBtn
          v-if="showChangeAvatarButton && !loading && !shouldShowLoaderForGeneration"
          color="primary"
          outlined
          class="mr-auto mr-md-2"
          elevation="0"
          @click="openAvatarDialog"
        >
          {{ $t('changeAvatarButton') }}
        </VBtn>

        <slot
          name="saveButton"
          :disabled="buttonSaveDisabled"
          :submit="saveAiQuestions"
        >
          <VBtn
            color="success"
            :disabled="buttonSaveDisabled"
            elevation="0"
            :loading="loading"
            @click="saveAiQuestions"
          >
            {{ $t('save') }}
          </VBtn>
        </slot>

        <slot
          name="publishButton"
          :disabled="buttonPublishDisabled"
          :submit="confirmQuestions"
        >
          <VBtn
            color="success"
            :disabled="buttonPublishDisabled"
            elevation="0"
            :loading="loading"
            @click="confirmQuestions"
          >
            {{ $t('next') }}
          </VBtn>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import useAiInterviewQuestions from '@/compositions/useAiInterviewQuestions';
import useVacancies from '@/compositions/useVacancies';
import {
  ref, toRefs, onMounted, watch, nextTick, computed, toRaw,
} from '@vue/composition-api';
import Draggable from 'vuedraggable';
import WizardCounts from '@/components/Vacancy/WizardCounts.vue';
import { useI18n } from 'vue-i18n-composable';
import {
  paymentTabs, settingsTabs, vacancyStatus, wizardSteps,
} from '@/enums';
import AiQuestion from '@/orm/models/AiInterview/AiQuestion';
import VacancyQuestionDestroyDialog from '@/components/Vacancy/VacancyQuestionDestroyDialog';
import VacancyPresetInterviewers from '@/components/Vacancy/VacancyPresetInterviewers.vue';
import isEqual from 'lodash.isequal';
import cloneDeep from 'lodash.clonedeep';
import Preloader from '../Preloader.vue';
import VacancyAiQuestionRow from './VacancyAiQuestionRow.vue';
import Vacancy from '@/orm/models/Vacancy/Vacancy';
import useDialog from '@/compositions/useDialog';

export default {
  name: 'VacancyAiQuestionsForm',
  components: {
    Preloader,
    Draggable,
    WizardCounts,
    VacancyAiQuestionRow,
    VacancyQuestionDestroyDialog,
    VacancyPresetInterviewers,
  },
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
    aiInterviewer: {
      type: String,
      required: false,
      default: null,
    },
    redactingAi: {
      type: Boolean,
      required: false,
      default: false,
    },
    maximumAiQuestions: {
      type: Number,
      required: false,
      default: 20,
    },
    selectedQuestions: {
      type: Array,
      required: false,
      default: null,
    },
    isAiTemplate: {
      type: Boolean,
      required: false,
      default: true,
    },
    avatars: {
      type: Object,
      required: false,
      default: null,
    },
  },
  setup(props, { emit, root }) {
    const {
      vacancy, aiInterviewer, redactingAi, maximumAiQuestions,
      selectedQuestions, isAiTemplate, avatars,
    } = toRefs(props);
    const { t } = useI18n();

    const {
      updateQuestions,
      confirm,
      getQuestions,
    } = useAiInterviewQuestions();

    const { publishAfterAiCompile } = useVacancies();

    const aiQuestionsInit = computed(() => {
      const questions = AiQuestion.query()
        .withAllRecursive()
        .where('vacancyId', vacancy?.value?.id)
        .get();
      
      return questions.sort((a, b) => {
        const orderA = a.questionOrder !== null && a.questionOrder !== undefined ? Number(a.questionOrder) : Infinity;
        const orderB = b.questionOrder !== null && b.questionOrder !== undefined ? Number(b.questionOrder) : Infinity;
        return orderA - orderB;
      });
    });

    const aiQuestionsGenerating = ref(false);
    const aiInterviewCompiling = ref(false);
    const aiInterviewRecompiling = ref(false);
    const aiQuestionsGenerated = ref(false);

    const aiQuestions = ref(null);
    const questionsUpdating = ref(false);
    const focusInput = ref(0);
    const loading = ref(false);
    const loadingText = ref(null);
    const destroyDialog = ref(false);
    const questionToDestroy = ref(null);
    const isFetchingQuestions = ref(false);
    const fetchTimeout = ref(null);
    const isFetchingVacancy = ref(false);
    const isNavigatingToNextStep = ref(false);
    const isSavingQuestions = ref(false);
    const avatarDialog = ref(false);
    const selectedAvatar = ref(null);
    const initialPublishedAvatarRef = ref(null);
    const savedAvatarRef = ref(null);

    function getAvatarForSave() {
      if (vacancy.value && vacancy.value.id) {
        const storedAvatar = localStorage.getItem(`vacancy_${vacancy.value.id}_avatar_for_edit`);
        if (storedAvatar) {
          return storedAvatar;
        }
      }
      return selectedAvatar.value || aiInterviewer.value;
    }

    const aiQuestionSomeEmpty = computed(() => {
      if (!aiQuestions.value || aiQuestions.value.length === 0) {
        return true;
      }
      return aiQuestions.value.some((item) => !item.text || !item.text.trim());
    });

    const currentAvatar = computed(() => {
      if (selectedAvatar.value) {
        return selectedAvatar.value;
      }
      if (vacancy.value && vacancy.value.id) {
        const storedAvatar = localStorage.getItem(`vacancy_${vacancy.value.id}_avatar_for_edit`);
        if (storedAvatar) {
          return storedAvatar;
        }
      }
      return aiInterviewer.value;
    });

    const publishedAvatar = computed(() => {
      if (vacancy.value?.isAiInterviewCompiled && vacancy.value?.script?.interviewer?.avatar) {
        return vacancy.value.script.interviewer.avatar;
      }
      if (vacancy.value?.avatar) {
        return vacancy.value.avatar;
      }
      return initialPublishedAvatarRef.value || aiInterviewer.value;
    });

    const publishedAvatarChanged = computed(() => {
      const current = currentAvatar.value;
      const published = publishedAvatar.value;
      if (!current && !published) {
        return false;
      }
      return current !== published;
    });

    const savedAiQuestionsNotDiffForEdit = computed(() => {
      if (!redactingAi.value) {
        return false;
      }
      if (!aiQuestions.value || !aiQuestionsInit.value || aiQuestionsInit.value.length === 0) {
        return false;
      }
      const initQuestions = aiQuestionsInit.value.map((q) => ({
        text: q.text || q.question || '',
      }));
      const currentQuestions = aiQuestions.value.map((q) => ({
        text: q.text || q.question || '',
      }));
      const questionsEqual = isEqual(initQuestions, currentQuestions);
      
      if (!questionsEqual) {
        return false;
      }
      
      const current = currentAvatar.value;
      let saved;
      
      if (savedAvatarRef.value !== null) {
        saved = savedAvatarRef.value;
      } else {
        saved = vacancy.value?.avatar || aiInterviewer.value;
      }
      
      return current === saved;
    });

    const publishedAiQuestionsNotDiffForEdit = computed(() => {
      if (!redactingAi.value) {
        return false;
      }
      
      if (!vacancy.value?.isAiInterviewCompiled) {
        return false;
      }
      
      let questionsEqual = false;
      
      if (vacancy.value?.script?.questions) {
        const scriptQuestions = toRaw(vacancy.value.script.questions || []).map((item) => ({
          question: item.question,
        }));

        const aiQuestionsMapped = toRaw(aiQuestions.value || []).map((item) => ({
          text: item.text,
        }));

        const normalizedAiQuestions = aiQuestionsMapped.map((item) => ({
          question: item.text,
        }));

        questionsEqual = isEqual(scriptQuestions, normalizedAiQuestions);
      } else {
        return false;
      }
      
      if (!questionsEqual) {
        return false;
      }
      
      return !publishedAvatarChanged.value;
    });

    const savedAiQuestionsNotDiffForCreate = computed(() => {
      if (redactingAi.value) {
        return false;
      }
      if (!aiQuestions.value || !aiQuestionsInit.value || aiQuestionsInit.value.length === 0) {
        return false;
      }
      const initQuestions = aiQuestionsInit.value.map((q) => ({
        text: q.text || q.question || '',
      }));
      const currentQuestions = aiQuestions.value.map((q) => ({
        text: q.text || q.question || '',
      }));
      const questionsEqual = isEqual(initQuestions, currentQuestions);
      
      return questionsEqual;
    });

    const publishedAiQuestionsNotDiffForCreate = computed(() => {
      if (redactingAi.value) {
        return false;
      }
      return false;
    });

    const disabled = computed(() => vacancy.value?.status === vacancyStatus.DRAFT && !aiInterviewRecompiling.value && aiQuestionsGenerated.value
      && (vacancy.value?.isAiInterviewCompiled || vacancy.value?.isAiInterviewCompiling));

    const aiQuestionsLimited = computed(() => maximumAiQuestions.value
      && (aiQuestions.value?.length > maximumAiQuestions.value));

    const showChangeAvatarButton = computed(() => {
      return vacancy.value && vacancy.value.isAiInterviewCompiled && redactingAi.value;
    });

    const buttonSaveDisabled = computed(() => {
      if (!aiQuestions.value || aiQuestions.value.length === 0) {
        return true;
      }

      const baseDisabled = questionsUpdating.value || aiInterviewCompiling.value
        || aiInterviewRecompiling.value || aiQuestionsGenerating.value
        || (vacancy.value?.status === vacancyStatus.DRAFT && vacancy.value?.isAiInterviewCompiled)
        || aiQuestionsLimited.value || aiQuestionSomeEmpty.value;

      if (redactingAi.value) {
        return baseDisabled || savedAiQuestionsNotDiffForEdit.value;
      }
      return baseDisabled || savedAiQuestionsNotDiffForCreate.value;
    });

    const buttonPublishDisabled = computed(() => {
      if (!aiQuestions.value || aiQuestions.value.length === 0) {
        return true;
      }

      const baseDisabled = questionsUpdating.value || aiInterviewRecompiling.value
        || aiQuestionsGenerating.value || aiQuestionsLimited.value || aiQuestionSomeEmpty.value;

      if (redactingAi.value) {
        return baseDisabled || (publishedAiQuestionsNotDiffForEdit.value && vacancy.value?.isAiInterviewCompiled);
      }
      return baseDisabled || publishedAiQuestionsNotDiffForCreate.value;
    });

    const buttonBackDisabled = computed(() => {
      if (redactingAi.value) {
        return aiInterviewCompiling.value || aiInterviewRecompiling.value;
      }
      return vacancy.value?.isAiInterviewCompiled
        || aiInterviewCompiling.value || aiInterviewRecompiling.value;
    });

    const vacancyAiQuestionsGenerating = computed(() => {
      return !vacancy.value?.aiQuestionsGenerated && vacancy.value?.aiQuestionsGenerating;
    });

    const vacancyAiQuestionsCompiling = computed(() => {
      return vacancy.value?.status === vacancyStatus.DRAFT
        && vacancy.value?.aiQuestionsGenerated
        && vacancy.value?.isAiInterviewCompiling
        && !vacancy.value?.isAiInterviewCompiled
        && !vacancy.value?.isAiInterviewRecompiling;
    });

    const vacancyAiQuestionsRecompiling = computed(() => {
      return vacancy.value?.isAiInterviewRecompiling && vacancy.value?.isAiInterviewCompiled;
    });

    const shouldEnableLoaderForQuestions = computed(() => {
      return !vacancy.value?.aiQuestionsGenerated && !aiQuestionsGenerating.value;
    });

    const shouldShowLoaderForGeneration = computed(() => {
      if (aiQuestionsInit.value?.length > 0) {
        return false;
      }
      return vacancyAiQuestionsGenerating.value
        || aiQuestionsGenerating.value
        || (!aiQuestionsInit.value?.length && isAiTemplate.value && !vacancy.value?.aiQuestionsGenerated && !aiQuestionsGenerating.value);
    });

    const shouldCompleteQuestionsGeneration = computed(() => {
      return vacancy.value?.aiQuestionsGenerated
        && !vacancy.value?.aiQuestionsGenerating
        && !aiInterviewCompiling.value
        && !vacancy.value?.isAiInterviewRecompiling
        && !redactingAi.value
        && isAiTemplate.value;
    });

    const shouldSetRecompiling = computed(() => {
      return vacancy.value?.isAiInterviewRecompiling;
    });

    const shouldCompleteRecompilation = computed(() => {
      return vacancy.value?.isAiInterviewCompiled
        && ((aiInterviewRecompiling.value && !vacancy.value?.isAiInterviewRecompiling));
    });

    const shouldCompleteCompilation = computed(() => {
      return vacancy.value?.isAiInterviewCompiled
        && ((aiInterviewCompiling.value && !vacancy.value?.isAiInterviewCompiling
          && !vacancy.value?.isAiInterviewRecompiling
        ));
    });

    const shouldDisableForm = computed(() => {
      return vacancy.value?.status === vacancyStatus.DRAFT
        && (vacancy.value?.isAiInterviewCompiled || vacancy.value?.isAiInterviewCompiling);
    });

    const shouldEnableLoaderForProcesses = computed(() => {
      return vacancy.value?.isAiInterviewCompiling
        || vacancy.value?.aiQuestionsGenerating
        || vacancy.value?.isAiInterviewRecompiling;
    });

    const shouldVacancyAiQuestionsGenerated = computed(() => {
      return vacancy.value?.status === vacancyStatus.DRAFT
        && vacancy.value?.aiQuestionsGenerated
        && vacancy.value?.isAiInterviewCompiling
        && !vacancy.value?.isAiInterviewCompiled
        && !vacancy.value?.isAiInterviewRecompiling;
    });

    const shouldHandleSpecialQuestions = computed(() => {
      return vacancy.value?.wizardState?.step === wizardSteps.SPECIAL
        && !isAiTemplate.value
        && !aiInterviewCompiling.value;
    });

    const shouldRegenerateQuestions = computed(() => {
      return isAiTemplate.value
        && vacancy.value.hasAiQuestions
        && !vacancy.value?.isAiInterviewRecompiling
        && !vacancy.value?.isAiInterviewCompiling
        && (vacancy.value?.aiInterviewPresetId || vacancy.value?.wizardState?.presetId)
        && !redactingAi.value;
    });

    const shouldLoadEmptyTemplateQuestions = computed(() => {
      return !aiQuestionsInit.value?.length && isAiTemplate.value;
    });

    const shouldContinueGeneratingQuestions = computed(() => {
      return !vacancy.value?.aiQuestionsGenerated && aiQuestionsGenerating.value;
    });

    const shouldFinishGenerationAndLoad = computed(() => {
      return vacancy.value?.aiQuestionsGenerated
        && !aiQuestionsGenerating.value
        && !aiInterviewCompiling.value
        && !vacancy.value?.isAiInterviewRecompiling;
    });

    const shouldStartGeneratingQuestions = computed(() => {
      return !vacancy.value?.aiQuestionsGenerated && !aiQuestionsGenerating.value;
    });

    async function fetchQuestions() {
      if (isFetchingQuestions.value) {
        return;
      }
      try {
        isFetchingQuestions.value = true;
        if (!loading.value) {
          setLoadingText('');
          enableLoader();
        } else if (isSavingQuestions.value || isNavigatingToNextStep.value) {
          setLoadingText('');
        }
        await getQuestions(vacancy.value.id);
        aiQuestions.value = cloneDeep(aiQuestionsInit.value);
        if (aiQuestionsInit.value?.length > 0 && !shouldEnableLoaderForProcesses.value && !isNavigatingToNextStep.value) {
          aiQuestionsGenerating.value = false;
          disableLoader();
        }
      } finally {
        isFetchingQuestions.value = false;
        if (loading.value && !shouldEnableLoaderForProcesses.value && aiQuestionsInit.value?.length > 0 && !isNavigatingToNextStep.value) {
          disableLoader();
        }
      }
    }

    async function fetchVacancyByIdWhenLoading() {
      if (!loading.value || isFetchingVacancy.value) {
        return;
      }
      try {
        isFetchingVacancy.value = true;
        if (aiQuestionsInit.value?.length && !aiQuestions.value?.length && !selectedQuestions.value?.length && !isFetchingQuestions.value) {
          await fetchQuestions();
        }
        emit('fetchById', vacancy.value.id);
      } finally {
        isFetchingVacancy.value = false;
      }
    }

    async function fetchVacancyWhileLoading() {
      if (!loading.value) {
        return;
      }
      if (fetchTimeout.value) {
        clearTimeout(fetchTimeout.value);
      }
      fetchTimeout.value = setTimeout(async () => {
        await fetchVacancyByIdWhenLoading();
        if (loading.value) {
          await fetchVacancyWhileLoading();
        }
      }, 10000);
    }

    function enableLoader() {
      if (loading.value) {
        return;
      }
      loading.value = true;
      fetchVacancyWhileLoading();
    }

    function disableLoader() {
      loading.value = false;
      if (fetchTimeout.value) {
        clearTimeout(fetchTimeout.value);
        fetchTimeout.value = null;
      }
    }

    watch(
      () => loading.value || shouldShowLoaderForGeneration.value,
      (isLoading) => {
        emit('setLoading', isLoading);
      },
      { immediate: true },
    );

    function setLoadingText(text) {
      loadingText.value = text;
    }

    function confirmAiQuestionChanges() {
      focusInput.value = 0;
    }

    function startAiQuestionCompiling() {
      aiInterviewCompiling.value = true;
    }

    function setFocusInput(aiQuestionId) {
      focusInput.value = aiQuestionId;
    }

    function addQuestion() {
      const initAiQuestion = {
        id: aiQuestions.value?.length + 1,
        text: '',
        vacancyId: vacancy.value.id,
      };
      aiQuestions.value.push(initAiQuestion);
      setFocusInput(initAiQuestion.id);
    }

    async function saveAiQuestions(skipFetch = false) {
      try {
        confirmAiQuestionChanges();
        if (!questionsUpdating.value) {
          questionsUpdating.value = true;
        }
        isSavingQuestions.value = true;
        setLoadingText('');
        emit('setSelectedQuestions', aiQuestions.value);

        const avatarToSave = getAvatarForSave();
        await updateQuestions(vacancy.value.id, {
          vacancyId: vacancy.value.id,
          questions: aiQuestions.value.map((question) => question.text || question.question),
          avatar: avatarToSave,
        });
        
        savedAvatarRef.value = avatarToSave;
        
        if (!skipFetch && !isFetchingQuestions.value) {
          await fetchQuestions();
        }
      } finally {
        if (!isNavigatingToNextStep.value) {
          questionsUpdating.value = false;
        }
        isSavingQuestions.value = false;
      }
    }

    async function confirmQuestions() {
      if (aiQuestionsLimited.value) {
        return false;
      }
      if (aiInterviewCompiling.value || aiQuestionsGenerating.value) {
        return false;
      }
      if (vacancy.value?.status === vacancyStatus.DRAFT
        && vacancy.value?.isAiInterviewCompiled
        && vacancy.value?.isAiInterviewRecompiling) {
        return false;
      }
      if (!aiInterviewer.value) {
        return false;
      }
      if (!aiQuestions.value || aiQuestions.value.length === 0) {
        root.$toast.error(t('addQuestionTextError'));
        return false;
      }

      setLoadingText('');
      isNavigatingToNextStep.value = true;
      enableLoader();

      questionsUpdating.value = true;
      startAiQuestionCompiling();

      try {
        await saveAiQuestions(true);
        await confirm(vacancy.value.id, {
          avatar: getAvatarForSave(),
        });
        await publishAfterAiCompile(vacancy.value.id);
        
        if (redactingAi.value) {
          emit('fetchById', vacancy.value.id);
          setLoadingText(t('pleaseWaitAiLoaderTextRecompiling'));
          aiInterviewRecompiling.value = true;
          isNavigatingToNextStep.value = false;
          questionsUpdating.value = false;
        } else {
          questionsUpdating.value = false;
        }
        
        return true;
      } catch (error) {
        aiInterviewCompiling.value = false;
        isNavigatingToNextStep.value = false;
        questionsUpdating.value = false;
        disableLoader();
        const errorMessage = error?.response?.data?.message || error?.message || t('errorOccurred') || 'Произошла ошибка';
        root.$toast.error(errorMessage);
        return false;
      }
    }

    async function redirectToUpgradePlan() {
      await root.$router.push({
        name: 'settings',
        params: { type: settingsTabs.PAYMENT, addType: paymentTabs.PLANS },
      });
    }

    async function fetchQuestionsWithRegenerate() {
      await getQuestions(vacancy.value.id, {
        regenerate: 1,
      });

      await fetchQuestions();
    }

    function showDestroyDialog(question) {
      questionToDestroy.value = question;
      destroyDialog.value = true;
    }

    function closeDestroyDialog() {
      destroyDialog.value = false;
    }

    function deleteAiQuestion() {
      aiQuestions.value = aiQuestions.value
        .filter(
          (q) => q.id !== questionToDestroy.value.id,
        );
      closeDestroyDialog();
    }

    function isAvatarInList(avatarId) {
      if (!avatarId || !avatars.value) {
        return false;
      }
      const filteredAvatars = Object.entries(avatars.value)
        .filter(([key]) => key !== '$id' && key !== 'id')
        .map(([, val]) => val);
      return filteredAvatars.some((avatar) => avatar.actor_id === avatarId);
    }

    function openAvatarDialog() {
      avatarDialog.value = true;
    }

    function handleAvatarSelected(interviewer) {
      if (!interviewer || !interviewer.actor_id) {
        return;
      }
      if (vacancy.value && vacancy.value.id && typeof vacancy.value.id === 'number') {
        localStorage.setItem(`vacancy_${vacancy.value.id}_avatar_for_edit`, interviewer.actor_id);
        selectedAvatar.value = interviewer.actor_id;
      } else {
        selectedAvatar.value = interviewer.actor_id;
      }
      avatarDialog.value = false;
    }

    watch(vacancy, async () => {
      if (isNavigatingToNextStep.value || isSavingQuestions.value || questionsUpdating.value) {
        return;
      }

      if (vacancyAiQuestionsGenerating.value) {
        setLoadingText(t('pleaseWaitAiLoaderText'));
      } else if (vacancyAiQuestionsCompiling.value) {
        setLoadingText(t('pleaseWaitAiLoaderTextVoice'));
      } else if (vacancyAiQuestionsRecompiling.value) {
        setLoadingText(t('pleaseWaitAiLoaderTextRecompiling'));
      }

      if (shouldEnableLoaderForQuestions.value) {
        enableLoader();
        if (!isFetchingQuestions.value) {
          await fetchQuestions();
        }
        if (aiQuestionsInit.value?.length > 0) {
          aiQuestionsGenerating.value = false;
          disableLoader();
        }
        return;
      }

      if (shouldCompleteQuestionsGeneration.value) {
        aiQuestionsGenerating.value = false;
        aiQuestionsGenerated.value = true;
        if (!isFetchingQuestions.value) {
          await fetchQuestions();
        }
        disableLoader();
        return;
      }

      if (shouldSetRecompiling.value) {
        aiInterviewRecompiling.value = true;
      }

      if (shouldCompleteRecompilation.value) {
        aiInterviewRecompiling.value = false;
        if (!isFetchingQuestions.value) {
          await fetchQuestions();
        }
        const publishedAvatarValue = vacancy.value?.script?.interviewer?.avatar 
          || vacancy.value?.avatar;
        if (publishedAvatarValue) {
          savedAvatarRef.value = publishedAvatarValue;
        }
        disableLoader();
        emit('setAiInterviewIsCompiled');
        return;
      }

      if (shouldCompleteCompilation.value) {
        aiInterviewCompiling.value = false;
        if (!isFetchingQuestions.value) {
          await fetchQuestions();
        }
        const publishedAvatarValue = vacancy.value?.script?.interviewer?.avatar 
          || vacancy.value?.avatar;
        if (publishedAvatarValue) {
          savedAvatarRef.value = publishedAvatarValue;
        }
        disableLoader();
        return;
      }

      if (vacancy.value?.isAiInterviewCompiled) {
        aiInterviewCompiling.value = false;
      }

      if (shouldDisableForm.value) {
        emit('setDisable');
      }

      if (shouldEnableLoaderForProcesses.value) {
        enableLoader();
      } else if (vacancy.value?.aiQuestionsGenerated
        && !vacancy.value?.isAiInterviewCompiling
        && !vacancy.value?.isAiInterviewRecompiling
        && !vacancy.value?.aiQuestionsGenerating
        && !vacancy.value?.isAiInterviewCompiled) {
        if (!aiQuestionsInit.value?.length && !loading.value && !isFetchingQuestions.value) {
          setLoadingText('');
          enableLoader();
          await fetchQuestions();
          disableLoader();
        } else if (loading.value && !isFetchingQuestions.value) {
          await fetchQuestions();
          if (aiQuestionsInit.value?.length > 0) {
            aiQuestionsGenerating.value = false;
            disableLoader();
          }
        } else if (aiQuestionsInit.value?.length > 0 && (loading.value || shouldShowLoaderForGeneration.value)) {
          aiQuestionsGenerating.value = false;
          disableLoader();
        }
      }
    });

    onMounted(async () => {
      aiQuestionsGenerating.value = vacancy.value?.aiQuestionsGenerating;
      aiInterviewCompiling.value = vacancy.value?.isAiInterviewCompiling;
      aiQuestionsGenerated.value = vacancy.value?.aiQuestionsGenerated;
      aiInterviewRecompiling.value = vacancy.value?.isAiInterviewRecompiling;

      if (!initialPublishedAvatarRef.value) {
        const publishedAvatarValue = vacancy.value?.script?.interviewer?.avatar 
          || vacancy.value?.avatar 
          || aiInterviewer.value;
        initialPublishedAvatarRef.value = publishedAvatarValue;
      }
      
      if (savedAvatarRef.value === null && redactingAi.value) {
        if (vacancy.value && vacancy.value.id) {
          if (vacancy.value?.avatar) {
            savedAvatarRef.value = vacancy.value.avatar;
          } else {
            const storedAvatar = localStorage.getItem(`vacancy_${vacancy.value.id}_avatar_for_edit`);
            if (storedAvatar) {
              savedAvatarRef.value = storedAvatar;
            }
          }
        }
      }

      if (vacancy.value && vacancy.value.id) {
        const storedAvatar = localStorage.getItem(`vacancy_${vacancy.value.id}_avatar_for_edit`);
        if (storedAvatar) {
          selectedAvatar.value = storedAvatar;
        }
      }

      if (vacancyAiQuestionsGenerating.value) {
        setLoadingText(t('pleaseWaitAiLoaderText'));
        enableLoader();
      }

      if (shouldHandleSpecialQuestions.value) {
        aiQuestions.value = cloneDeep(selectedQuestions.value).map((question) => ({
          ...question,
          text: question.question && !question.text ? question.question : question.text,
        }));
        disableLoader();
        return;
      }

      if (shouldRegenerateQuestions.value) {
        await fetchQuestionsWithRegenerate();
        emit('resetWizardPresetid');
        return;
      }

      if (shouldLoadEmptyTemplateQuestions.value) {
        if (shouldStartGeneratingQuestions.value) {
          setLoadingText(t('pleaseWaitAiLoaderText'));
          enableLoader();
          aiQuestionsGenerating.value = true;
        }
        if (!isFetchingQuestions.value) {
          await fetchQuestions();
        }
        return;
      }

      if (shouldContinueGeneratingQuestions.value) {
        setLoadingText(t('pleaseWaitAiLoaderText'));
        enableLoader();
        aiQuestionsGenerating.value = true;
        return;
      }

      if (shouldFinishGenerationAndLoad.value) {
        if (!isFetchingQuestions.value) {
          await fetchQuestions();
        }
        disableLoader();
        return;
      }

      if (shouldStartGeneratingQuestions.value) {
        enableLoader();
        aiQuestionsGenerating.value = true;
        if (!isFetchingQuestions.value) {
          await fetchQuestions();
        }
        return;
      }

      if (shouldEnableLoaderForProcesses.value) {
        if (vacancyAiQuestionsRecompiling.value) {
          setLoadingText(t('pleaseWaitAiLoaderTextRecompiling'));
        } else if (shouldVacancyAiQuestionsGenerated.value) {
          setLoadingText(t('pleaseWaitAiLoaderTextVoice'));
        } else if (vacancyAiQuestionsGenerating.value) {
          setLoadingText(t('pleaseWaitAiLoaderText'));
        }
        enableLoader();
        return;
      }


      if (shouldDisableForm.value) {
        emit('setDisable');
      }

      if (vacancy.value?.isAiInterviewRecompiling) {
        aiInterviewCompiling.value = true;
      }

      if (vacancy.value?.isAiInterviewCompiled && redactingAi.value && avatars.value) {
        const currentAvatar = vacancy.value?.avatar || aiInterviewer.value;
        if (currentAvatar && !isAvatarInList(currentAvatar)) {
          root.$toast.warning(t('avatarNotFound'));
          avatarDialog.value = true;
        }
      }
    });

    watch([avatars, vacancy], () => {
      if (vacancy.value?.isAiInterviewCompiled && redactingAi.value && avatars.value) {
        const currentAvatar = vacancy.value?.avatar || aiInterviewer.value;
        if (currentAvatar && !isAvatarInList(currentAvatar)) {
          root.$toast.warning(t('avatarNotFound'));
          avatarDialog.value = true;
        }
      }
    });

    return {
      aiQuestions,
      setFocusInput,
      deleteAiQuestion,
      saveAiQuestions,
      confirmQuestions,
      questionsUpdating,
      focusInput,
      loading,
      loadingText,
      aiInterviewCompiling,
      disabled,
      confirmAiQuestionChanges,
      buttonSaveDisabled,
      buttonBackDisabled,
      buttonPublishDisabled,
      destroyDialog,
      showDestroyDialog,
      closeDestroyDialog,
      aiQuestionsLimited,
      redirectToUpgradePlan,
      addQuestion,
      aiQuestionsInit,
      shouldShowLoaderForGeneration,
      avatarDialog,
      openAvatarDialog,
      handleAvatarSelected,
      isAvatarInList,
      showChangeAvatarButton,
      getAvatarForSave,
    };
  },
};
</script>

<style lang="scss" scoped>
.q-row {
  background: #fff;
  margin-bottom: 5px;
  border-radius: 5px;
}

.exceed-limit {
  border: 1px solid #EA0D0D;
}

.h-100 {
  height: 100%;
}

.limit-questions {
  display: flex;
  gap: 12px;
  &__main-wrap {
    &_title {
      color: #212121;
      font-size: 14px;
      font-weight: 400;
      line-height: 16px;
    }
    &_btn {
      margin-top: 12px;
      font-weight: 400 !important;
    }
    &_text {
      margin-top: 7px;
      color: #666666;
      font-size: 12px;
      line-height: 18px;
      font-weight: 300;
    }
  }
}
</style>

<i18n locale="en" lang="yaml">
step: "Interview questions list"
publish: Publish
lostInfoWarning: Changes not saved will be lost. Are you sure?
continue: Continue editing
goBack: Go back
returnToVacancy: Go back to the job
congrats: You have completed question selection.
anyTime: You can delete a question at any time, swap questions or add a new question.
pleaseWaitAiLoaderText: 'Artificial intelligence is in the process of selecting questions for your job.
The expected wait time is 3-5 minutes. Please hold on. If necessary, you can go to other sections.
The job will be available in the jobs section after completion of the preparation process.'
pleaseWaitAiLoaderTextVoice: 'Artificial intelligence is in the process of voicing questions for your job.
We expect it to take 3-5 minutes, please wait... If necessary, you can go to other sections.
The job will be available in the jobs section after completion of the preparation process'
pleaseWaitAiLoaderTextRecompiling: 'Artificial intelligence is in the process of voicing additional questions for your job.
The expected time is 3-5 minutes, please wait… If necessary, you can go to other sections.
The job will be available in the jobs section after completing the preparation process.'
pleaseWaitAiLoaderTextCopy: 'The job is in the process of copying. The expected time is 1-3 minutes. Please wait…'
save: Save
addQuestion: Add question
addQuestionTextError: Add at least one question for the vacancy.
maxAiQuestions: 'The limit of questions from the AI has been reached. The maximum number of questions is {count} To remove the limit, you need to increase the tariff plan.'
toUpgradePlan: Increase the tariff
maxQuestionsAvailable: 'Only {count} questions per job are allowed'
maxQuestionsAvailableText: To post the job, either remove some questions.
changeAvatar: Change avatar
changeAvatarButton: Change avatar
avatarNotFound: Avatar not found in the list. Please select a new avatar.
avatarChanged: Avatar successfully changed
</i18n>

<i18n locale="ru" lang="yaml">
step: "Список вопросов для интервью"
publish: Опубликовать
lostInfoWarning: Все изменения будут потеряны. Вы уверены?
continue: Продолжить редактирование
goBack: Назад
returnToVacancy: Вернуться к вакансии
congrats: Ура! ИИ завершил подборку вопросов для вашей вакансии
anyTime: В любой момент вы можете удалить вопрос, поменять вопросы местами или добавить новый.
pleaseWaitAiLoaderText: 'Искусственный интеллект в процессе подбора вопросов для вашей вакансии.
Ожидаемое время 3-5 минут, пожалуйста, подождите… При необходимости вы можете перейти в другие разделы.
Вакансия будет доступна в разделе вакансий после завершения процесса подготовки.'
pleaseWaitAiLoaderTextVoice: 'Искусственный интеллект в процессе озвучивания вопросов для вашей вакансии.
Ожидаемое время 3-5 минут, пожалуйста, подождите… При необходимости вы можете перейти в другие разделы.
Вакансия будет доступна в разделе вакансий после завершения процесса подготовки.'
pleaseWaitAiLoaderTextRecompiling: 'Искусственный интеллект в процессе озвучивания дополнительных вопросов для вашей вакансии.
Ожидаемое время 3-5 минут, пожалуйста, подождите… При необходимости Вы можете перейти в другие разделы.
Вакансия будет доступна в разделе вакансий, после завершения процесса подготовки.'
pleaseWaitAiLoaderTextCopy: 'Вакансия в процессе копирования. Ожидаемое время 1-3 минуты. Пожалуйста, подождите…'
save: Сохранить
addQuestion: Добавить вопрос
addQuestionTextError: Добавьте минимум один вопрос для вакансии.
maxAiQuestions: 'Достигнут лимит вопросов от ИИ. Максимальное кол-во вопросов - {count} Для снятия лимита необходимо повысить тарифный план.'
toUpgradePlan: Повысить тариф
maxQuestionsAvailable: 'Доступно только {count} вопросов к вакансии'
maxQuestionsAvailableText: Для размещения вакансии удалите часть вопросов.
changeAvatar: Сменить аватар
changeAvatarButton: Изменить аватар
avatarNotFound: Аватар не найден в списке. Пожалуйста, выберите новый аватар.
avatarChanged: Аватар успешно изменен
</i18n>
